import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  Coins,
  Globe,
  LogOut,
  Menu as MenuIcon,
  Moon,
  Settings,
  Sun,
  Timer,
  Trophy,
  User,
} from 'lucide-react';
import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';

import { notificationQueries } from '../../api/resources/notifications';
import { useSession } from '../../app/session';
import { useSite } from '../../app/site';
import { useTheme } from '../../app/theme';
import { LOCALE_LABELS, SUPPORTED_LOCALES } from '../../i18n';
import { cn } from '../../lib/cn';
import { useLocale } from '../../lib/useLocale';
import { Avatar } from '../ui/Avatar';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Drawer } from '../ui/Drawer';
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger, Tooltip } from '../ui/Menu';
import { Logo, LogoMark } from './Logo';
import { SiteNavAnchor } from './SiteNavAnchor';

interface NavItem {
  to: string;
  label: string;
  /** Hidden when the server reports the matching feature as closed. */
  flag?: 'community';
}

export function Header() {
  const { t, locale, setLocale, text } = useLocale();
  const { settings, features } = useSite();
  const { viewer, isAuthenticated, logout } = useSession();
  const { resolved, setPreference } = useTheme();
  const [isMobileOpen, setMobileOpen] = useState(false);

  const unreadQuery = useQuery({ ...notificationQueries.unreadCount(), enabled: isAuthenticated });
  const unread = isAuthenticated ? (unreadQuery.data ?? viewer?.unreadNotifications ?? 0) : 0;

  const navItems: NavItem[] = [
    { to: '/achievements', label: t('nav.achievements') },
    { to: '/leaderboard', label: t('nav.leaderboard') },
    { to: '/community', label: t('nav.community'), flag: 'community' },
    { to: '/players', label: t('nav.players') },
  ];
  const visibleNav = navItems.filter((item) => !item.flag || features[item.flag]);
  const headerLinks = settings?.navLinks.header ?? [];

  const siteName = settings?.siteName ?? t('common.appName');

  return (
    <header className="border-line bg-canvas-veil top-0 z-30 border-b backdrop-blur-xl lg:sticky">
      <div className="max-w-8xl mx-auto flex h-16 min-w-0 items-center gap-2 px-4 sm:gap-3 sm:px-6 lg:px-8">
        <Link to="/" className="shrink-0 rounded-sm" aria-label={siteName}>
          <Logo siteName={siteName} />
        </Link>

        <nav className="ml-4 hidden items-center gap-0.5 lg:flex" aria-label={t('nav.menu')}>
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'relative rounded-sm px-3 py-2 text-sm font-medium transition-colors duration-[160ms]',
                  isActive ? 'text-text' : 'text-text-subtle hover:text-text',
                  isActive &&
                    'after:bg-primary after:absolute after:inset-x-3 after:-bottom-[13px] after:h-0.5 after:rounded-full',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
          {headerLinks.map((link) => (
            <SiteNavAnchor
              key={link.code || link.id}
              href={link.url}
              className="text-text-subtle hover:text-text relative rounded-sm px-3 py-2 text-sm font-medium transition-colors duration-[160ms]"
            >
              {text(link.name) || link.nameLocalized}
            </SiteNavAnchor>
          ))}
        </nav>

        <div className="ml-auto flex min-w-0 items-center gap-1 sm:gap-1.5">
          {isAuthenticated ? (
            <Tooltip content={t('nav.notifications')}>
              <Link
                to="/notifications"
                className="text-text-subtle hover:bg-surface-overlay hover:text-text relative inline-flex size-9 items-center justify-center rounded-md transition-colors"
                aria-label={
                  unread > 0
                    ? `${t('nav.notifications')} · ${t('notifications.unreadBadge', { count: unread })}`
                    : t('nav.notifications')
                }
              >
                <Bell className="size-4.5" />
                {unread > 0 ? (
                  <span className="numeric bg-danger-strong text-danger-contrast absolute -top-0.5 -right-0.5 flex min-w-4 items-center justify-center rounded-full px-1 text-[0.625rem] leading-4 font-semibold">
                    {unread > 99 ? '99+' : unread}
                  </span>
                ) : null}
              </Link>
            </Tooltip>
          ) : null}

          <Menu>
            <MenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={t('common.language')}>
                <Globe className="size-4.5" />
              </Button>
            </MenuTrigger>
            <MenuContent>
              <MenuLabel>{t('common.language')}</MenuLabel>
              {SUPPORTED_LOCALES.map((candidate) => (
                <MenuItem
                  key={candidate}
                  onSelect={() => void setLocale(candidate)}
                  className={candidate === locale ? 'text-primary' : undefined}
                >
                  {LOCALE_LABELS[candidate]}
                </MenuItem>
              ))}
              <MenuSeparator />
              <MenuLabel>{t('common.theme')}</MenuLabel>
              <MenuItem icon={<Moon className="size-4" />} onSelect={() => setPreference('dark')}>
                {t('common.themeDark')}
              </MenuItem>
              <MenuItem icon={<Sun className="size-4" />} onSelect={() => setPreference('light')}>
                {t('common.themeLight')}
              </MenuItem>
            </MenuContent>
          </Menu>

          <Tooltip content={resolved === 'dark' ? t('common.themeLight') : t('common.themeDark')}>
            <Button
              variant="ghost"
              size="icon"
              className="hidden sm:inline-flex"
              onClick={() => setPreference(resolved === 'dark' ? 'light' : 'dark')}
              aria-label={t('common.theme')}
            >
              {resolved === 'dark' ? <Sun className="size-4.5" /> : <Moon className="size-4.5" />}
            </Button>
          </Tooltip>

          {isAuthenticated && viewer ? (
            <Menu>
              <MenuTrigger asChild>
                <button
                  type="button"
                  className="hover:bg-surface-overlay ml-1 flex items-center gap-2 rounded-full p-0.5 pr-2.5 transition-colors"
                >
                  <Avatar src={viewer.avatarUrl} name={viewer.nickname} size="sm" />
                  <span className="hidden max-w-28 truncate text-sm font-medium md:block">
                    {viewer.nickname}
                  </span>
                </button>
              </MenuTrigger>
              <MenuContent className="min-w-60">
                <div className="flex items-center gap-3 px-2.5 py-2">
                  <Avatar src={viewer.avatarUrl} name={viewer.nickname} size="md" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{viewer.nickname}</p>
                    <p className="numeric text-2xs text-text-faint truncate">{viewer.publicPlayerId}</p>
                  </div>
                </div>
                <div className="bg-surface-sunken mx-2.5 mb-1 flex items-center justify-between rounded-sm px-2.5 py-1.5">
                  <span className="text-text-subtle text-xs">{t('common.points')}</span>
                  <span className="numeric text-gold text-sm font-semibold">{viewer.points}</span>
                </div>
                <MenuSeparator />
                <MenuItem asChild>
                  <Link to={`/players/${viewer.publicPlayerId}`}>
                    <User className="size-4" />
                    {t('nav.myProfile')}
                  </Link>
                </MenuItem>
                <MenuItem asChild>
                  <Link to="/achievements">
                    <Trophy className="size-4" />
                    {t('nav.achievements')}
                  </Link>
                </MenuItem>
                <MenuItem asChild>
                  <Link to="/speedruns">
                    <Timer className="size-4" />
                    {t('nav.speedruns')}
                  </Link>
                </MenuItem>
                <MenuItem asChild>
                  <Link to="/points">
                    <Coins className="size-4" />
                    {t('nav.points')}
                  </Link>
                </MenuItem>
                <MenuItem asChild>
                  <Link to="/settings">
                    <Settings className="size-4" />
                    {t('nav.settings')}
                  </Link>
                </MenuItem>
                <MenuSeparator />
                <MenuItem tone="danger" icon={<LogOut className="size-4" />} onSelect={() => void logout()}>
                  {t('common.signOut')}
                </MenuItem>
              </MenuContent>
            </Menu>
          ) : (
            <div className="ml-1 flex items-center gap-2">
              <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
                <Link to="/login">{t('common.signIn')}</Link>
              </Button>
              <Button asChild size="sm">
                <Link to="/register">{t('common.signUp')}</Link>
              </Button>
            </div>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-expanded={isMobileOpen}
            aria-controls="app-nav-drawer"
            aria-label={t('nav.menu')}
          >
            <MenuIcon className="size-5" />
          </Button>
        </div>
      </div>

      <Drawer
        open={isMobileOpen}
        onOpenChange={setMobileOpen}
        side="left"
        closeLabel={t('common.close')}
        title={
          <span className="flex items-center gap-2.5">
            <span aria-hidden="true">
              <LogoMark className="size-7" />
            </span>
            <span className="font-display">{siteName}</span>
          </span>
        }
      >
        <nav
          id="app-nav-drawer"
          aria-label={t('nav.menu')}
          onClick={(event) => {
            if ((event.target as HTMLElement).closest('a')) setMobileOpen(false);
          }}
        >
          <ul className="space-y-0.5">
            {visibleNav.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      'block rounded-sm px-3 py-2.5 text-sm font-medium',
                      isActive ? 'bg-primary-soft text-primary' : 'text-text-muted hover:bg-surface-overlay',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
            {headerLinks.map((link) => (
              <li key={link.code || link.id}>
                <SiteNavAnchor
                  href={link.url}
                  className="text-text-muted hover:bg-surface-overlay block rounded-sm px-3 py-2.5 text-sm font-medium"
                >
                  {text(link.name) || link.nameLocalized}
                </SiteNavAnchor>
              </li>
            ))}
            {isAuthenticated ? (
              <>
                <li className="bg-line my-2 h-px" />
                {[
                  { to: '/speedruns', label: t('nav.speedruns') },
                  { to: '/points', label: t('nav.points') },
                  { to: '/notifications', label: t('nav.notifications') },
                  { to: '/settings', label: t('nav.settings') },
                ].map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center justify-between rounded-sm px-3 py-2.5 text-sm font-medium',
                          isActive
                            ? 'bg-primary-soft text-primary'
                            : 'text-text-muted hover:bg-surface-overlay',
                        )
                      }
                    >
                      {item.label}
                      {item.to === '/notifications' && unread > 0 ? (
                        <Badge tone="danger">{unread}</Badge>
                      ) : null}
                    </NavLink>
                  </li>
                ))}
              </>
            ) : (
              <li className="pt-2">
                <Button asChild variant="outline" size="md" className="w-full">
                  <Link to="/login">{t('common.signIn')}</Link>
                </Button>
              </li>
            )}
          </ul>
        </nav>
      </Drawer>
    </header>
  );
}
